import { useEffect, useMemo, useState, type ReactNode } from "react";
import { requestApi } from "../../core/api/api";
import {
	clearSession,
	setSession,
	useSession,
	type ServixUserType,
} from "../../core/session/session";
import { AuthSessionContext, type AuthSessionContextValue } from "./auth-session-store";

// ── Types ─────────────────────────────────────────────────────────────────────

type VerifyResponse = {
	isAuthenticated: boolean;
	userId?: number;
	role?: string | null;
	message?: string;
};

type UserPayload = {
	user?: {
		id?: number;
		names?: string | null;
		last_names?: string | null;
		email?: string | null;
		type_user?: unknown;
	};
	id?: number;
	names?: string | null;
	last_names?: string | null;
	email?: string | null;
	type_user?: unknown;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalizeUserType(value: unknown): ServixUserType | null {
	if (typeof value !== "string") return null;
	const raw = value.trim().toLowerCase();
	if (raw === "cliente" || raw === "client") return "Cliente";
	if (raw === "proveedor" || raw === "provider") return "Proveedor";
	if (raw === "gerente" || raw === "manager") return "Gerente";
	if (raw === "soporte" || raw === "support") return "Soporte";
	return null;
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthSessionProvider({ children }: { children: ReactNode }) {
	const session = useSession();
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;

		async function hydrate() {
			try {
				// { auth: false } prevents forceLogoutToLogin() on 401 (cookie expired/missing)
				const verify = await requestApi<VerifyResponse>(
					"GET",
					"/session/verify",
					undefined,
					{ auth: false },
				);

				if (cancelled) return;

				if (!verify.isAuthenticated || !verify.userId) {
					clearSession();
					return;
				}

				const userPayload = await requestApi<UserPayload>(
					"GET",
					`/users/${verify.userId}`,
				);

				if (cancelled) return;

				const entity = userPayload.user ?? userPayload;
				const name =
					[entity.names, entity.last_names].filter(Boolean).join(" ") || null;
				const typeUser =
					normalizeUserType(entity.type_user) ??
					normalizeUserType(verify.role) ??
					null;

				setSession({
					user: {
						id: verify.userId,
						email: entity.email ?? "",
						type_user: typeUser,
						name,
					},
				});
			} catch {
				if (!cancelled) clearSession();
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void hydrate();
		return () => {
			cancelled = true;
		};
	}, []);

	const role = session?.user?.type_user ?? null;
	const isAuthenticated = Boolean(session?.user?.id);
	const status: "authenticated" | "anonymous" = isAuthenticated
		? "authenticated"
		: "anonymous";

	const value = useMemo<AuthSessionContextValue>(
		() => ({ session, isAuthenticated, role, status, isLoading }),
		[session, isAuthenticated, role, status, isLoading],
	);

	return (
		<AuthSessionContext.Provider value={value}>
			{children}
		</AuthSessionContext.Provider>
	);
}
